import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import { useChatStore } from "./chatStore";
import { ChatSessionItem } from "./ChatSessionItem";
import {
  deleteSession,
  listSessions,
  renameSession,
  searchSessions,
} from "./chatApi";
import type { ChatSession, SearchResult } from "./types";

export const ChatSidebar: React.FC = () => {
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const sessions = useChatStore((s) => s.sessions);
  const activeSession = useChatStore((s) => s.activeSession);
  const setSessions = useChatStore((s) => s.setSessions);
  const setActiveSession = useChatStore((s) => s.setActiveSession);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Fetch sessions on open
  useEffect(() => {
    if (!sidebarOpen) return;
    setLoading(true);
    listSessions()
      .then((data) => setSessions(data.results))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sidebarOpen, setSessions]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchSessions(searchQuery.trim())
        .then((data) => setSearchResults(data.results))
        .catch(() => setSearchResults([]))
        .finally(() => setIsSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleNewChat = useCallback(() => {
    setActiveSession(null);
    setSearchQuery("");
    setSidebarOpen(false);
  }, [setActiveSession, setSidebarOpen]);

  const handleSelect = useCallback(
    (session: ChatSession) => {
      setActiveSession(session);
      setSearchQuery("");
      setSidebarOpen(false);
    },
    [setActiveSession, setSidebarOpen],
  );

  const handleRename = useCallback(
    async (session: ChatSession, title: string) => {
      const prev = [...sessions];
      setSessions(sessions.map((s) => (s.id === session.id ? { ...s, title } : s)));
      try {
        await renameSession(session.id, title);
      } catch {
        setSessions(prev);
      }
    },
    [sessions, setSessions],
  );

  const handleDelete = useCallback(
    async (session: ChatSession) => {
      const prev = [...sessions];
      setSessions(sessions.filter((s) => s.id !== session.id));
      if (activeSession?.id === session.id) setActiveSession(null);
      try {
        await deleteSession(session.id);
      } catch {
        setSessions(prev);
      }
    },
    [sessions, setSessions, activeSession, setActiveSession],
  );

  if (!sidebarOpen) return null;

  const displayList = searchQuery.trim()
    ? searchResults.map((r) => r.session)
    : sessions;

  return (
    <div
      className="absolute inset-0 z-10 flex flex-col bg-background"
      style={{ width: "100%" }}
    >
      {/* New Chat button */}
      <div className="p-3 shrink-0">
        <button
          type="button"
          onClick={handleNewChat}
          className="w-full flex items-center justify-center gap-2 px-3 h-8 bg-primary text-primary-foreground rounded-full text-xs font-bold hover:scale-[1.02] active:scale-[0.98] transition-transform"
        >
          <Plus className="size-3.5" />
          New chat
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2 shrink-0">
        <div className="flex items-center gap-2 px-2 h-8 rounded-md border border-border bg-muted/50">
          <Search className="size-3.5 text-muted-foreground shrink-0" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className="flex-1 min-w-0 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* Session list */}
      <nav className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
        {loading && !searchQuery.trim() && (
          <div className="space-y-2 px-2 py-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 bg-muted/50 rounded-md animate-pulse" />
            ))}
          </div>
        )}
        {isSearching && (
          <p className="px-2 py-4 text-xs text-muted-foreground text-center">
            Searching...
          </p>
        )}
        {!loading && !isSearching && displayList.length === 0 && (
          <p className="px-2 py-4 text-xs text-muted-foreground text-center">
            {searchQuery.trim() ? "No results found" : "No conversations yet"}
          </p>
        )}
        {!loading &&
          !isSearching &&
          displayList.map((session) => (
            <ChatSessionItem
              key={session.id}
              session={session}
              selected={session.id === activeSession?.id}
              onClick={() => handleSelect(session)}
              onRename={(title) => handleRename(session, title)}
              onDelete={() => handleDelete(session)}
            />
          ))}
      </nav>
    </div>
  );
};
